import type { NavKey } from "./types";

/** cPanel tool for home icon grid */
export type CpanelTool = {
  id: string;
  label: string;
  description: string;
  section: CpanelSectionId;
  nav: NavKey;
  /** Feature list key (defaults to nav) */
  feature?: string;
  icon: string;
  /** Deep-link within a page (e.g. FTP tab) */
  hint?: string;
};

export type CpanelSectionId =
  | "home"
  | "mail"
  | "domains"
  | "ssl"
  | "files"
  | "ftp"
  | "databases"
  | "metrics"
  | "security"
  | "software"
  | "advanced"
  | "preferences";

export type CpanelSection = {
  id: CpanelSectionId;
  label: string;
  nav?: NavKey;
};

/** Left sidebar — cPanel information architecture (Webuzo-like labels) */
export const CPANEL_SECTIONS: CpanelSection[] = [
  { id: "home", label: "Home", nav: "overview" },
  { id: "domains", label: "Domain", nav: "sites" },
  { id: "ssl", label: "SSL/TLS", nav: "ssl" },
  { id: "databases", label: "Database", nav: "databases" },
  { id: "files", label: "File Manager", nav: "files" },
  { id: "ftp", label: "FTP", nav: "ftp" },
  { id: "software", label: "Applications", nav: "apps" },
  { id: "mail", label: "Email", nav: "email" },
  { id: "advanced", label: "Configuration", nav: "cron" },
  { id: "security", label: "Security", nav: "security" },
  { id: "metrics", label: "Server Utilities", nav: "logs" },
  { id: "preferences", label: "Preferences", nav: "polish" }
];

/** Sidebar icon glyphs */
export const CPANEL_SECTION_ICONS: Record<string, string> = {
  home: "⌂",
  domains: "◎",
  ssl: "🔒",
  databases: "⛁",
  files: "📁",
  ftp: "⇪",
  software: "✦",
  mail: "✉",
  advanced: "⚙",
  security: "⛨",
  metrics: "🔧",
  preferences: "◐"
};

export const CPANEL_TOOLS: CpanelTool[] = [
  // Domain
  {
    id: "domains",
    label: "Manage Domains",
    description: "List and manage domains",
    section: "domains",
    nav: "sites",
    feature: "sites",
    icon: "◎"
  },
  {
    id: "add-domain",
    label: "Add Domain",
    description: "Add a new domain",
    section: "domains",
    nav: "sites",
    feature: "sites",
    icon: "＋"
  },
  {
    id: "addon-domain",
    label: "Add Addon Domain",
    description: "Addon domains",
    section: "domains",
    nav: "sites",
    feature: "sites",
    icon: "▣"
  },
  {
    id: "subdomains",
    label: "Subdomains",
    description: "Create subdomains",
    section: "domains",
    nav: "sites",
    feature: "sites",
    icon: "◉"
  },
  {
    id: "aliases",
    label: "Aliases",
    description: "Parked / alias domains",
    section: "domains",
    nav: "sites",
    feature: "sites",
    icon: "≡"
  },
  {
    id: "zone-editor",
    label: "DNS Zone Settings",
    description: "A, CNAME, MX, TXT",
    section: "domains",
    nav: "dns",
    feature: "dns",
    icon: "⬡"
  },
  // SSL/TLS — dedicated top-level (Webuzo-style)
  {
    id: "auto-ssl",
    label: "Automatic SSL",
    description: "One-click Let's Encrypt · RSA 2048-bit",
    section: "ssl",
    nav: "ssl",
    feature: "sites",
    icon: "🔒"
  },
  {
    id: "ssl-install",
    label: "Install Certificate",
    description: "Install SSL for a domain",
    section: "ssl",
    nav: "ssl",
    feature: "sites",
    icon: "＋"
  },
  {
    id: "ssl-certs",
    label: "Certificates",
    description: "View certificate list",
    section: "ssl",
    nav: "ssl",
    feature: "sites",
    icon: "📜"
  },
  // Database
  {
    id: "mysql",
    label: "Manage Databases",
    description: "Create databases",
    section: "databases",
    nav: "databases",
    feature: "databases",
    icon: "⛁"
  },
  {
    id: "phpmyadmin",
    label: "phpMyAdmin",
    description: "Full phpMyAdmin (SSO)",
    section: "databases",
    nav: "databases",
    feature: "databases",
    icon: "PH"
  },
  {
    id: "db-users",
    label: "Database Users",
    description: "DB users & grants",
    section: "databases",
    nav: "databases",
    feature: "databases",
    icon: "👤"
  },
  // File Manager (separate from FTP)
  {
    id: "file-manager",
    label: "File Manager",
    description: "Browse website files",
    section: "files",
    nav: "files",
    feature: "files",
    icon: "📁"
  },
  {
    id: "backup",
    label: "Backup",
    description: "Backup & restore",
    section: "files",
    nav: "backups",
    feature: "backups",
    icon: "⇩"
  },
  // FTP (Webuzo-style: Manage / Add / Connections)
  {
    id: "manage-ftp",
    label: "Manage FTP",
    description: "List FTP users, usage and quota",
    section: "ftp",
    nav: "ftp",
    feature: "files",
    icon: "⇪",
    hint: "manage"
  },
  {
    id: "add-ftp",
    label: "Add FTP Account",
    description: "Create a new FTP user",
    section: "ftp",
    nav: "ftp",
    feature: "files",
    icon: "＋",
    hint: "add"
  },
  {
    id: "ftp-connections",
    label: "FTP Connections",
    description: "Active FTP sessions",
    section: "ftp",
    nav: "ftp",
    feature: "files",
    icon: "◎",
    hint: "connections"
  },
  // Applications
  {
    id: "apps",
    label: "Install Apps",
    description: "WordPress & more",
    section: "software",
    nav: "apps",
    feature: "apps",
    icon: "WP"
  },
  {
    id: "softaculous",
    label: "Application Manager",
    description: "Manage installs",
    section: "software",
    nav: "apps",
    feature: "apps",
    icon: "✦"
  },
  // Email
  {
    id: "email-accounts",
    label: "Email Accounts",
    description: "Mailboxes",
    section: "mail",
    nav: "email",
    feature: "email",
    icon: "✉"
  },
  {
    id: "forwarders",
    label: "Forwarders",
    description: "Mail forwarding",
    section: "mail",
    nav: "email",
    feature: "email",
    icon: "↪"
  },
  // Configuration
  {
    id: "cron",
    label: "Cron Jobs",
    description: "Scheduled tasks",
    section: "advanced",
    nav: "cron",
    feature: "cron",
    icon: "⏱"
  },
  {
    id: "import-export",
    label: "Import / Export",
    description: "Transfer data",
    section: "advanced",
    // End-users stay on cron/config area (migrations is staff-only)
    nav: "cron",
    feature: "cron",
    icon: "⇄"
  },
  // Security
  {
    id: "password",
    label: "Password & Security",
    description: "Password and 2FA",
    section: "security",
    nav: "security",
    feature: "security",
    icon: "⚿"
  },
  {
    id: "ssh",
    label: "SSH Access",
    description: "Keys & shell",
    section: "security",
    nav: "security",
    feature: "security",
    icon: "⌨"
  },
  // Server utilities / metrics
  {
    id: "errors",
    label: "Error Logs",
    description: "Site errors",
    section: "metrics",
    nav: "logs",
    feature: "logs",
    icon: "⚠"
  },
  {
    id: "raw-logs",
    label: "Access Logs",
    description: "Raw access logs",
    section: "metrics",
    nav: "logs",
    feature: "logs",
    icon: "≣"
  },
  // Preferences
  {
    id: "appearance",
    label: "Appearance",
    description: "Theme options",
    section: "preferences",
    nav: "polish",
    feature: "polish",
    icon: "◐"
  }
];

const FAV_KEY = "realpanel_cpanel_fav_v1";

export function loadCpanelFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    if (!raw) {
      return [
        "file-manager",
        "email-accounts",
        "mysql",
        "phpmyadmin",
        "apps",
        "domains"
      ];
    }
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return ["file-manager", "email-accounts", "mysql", "apps"];
  }
}

export function toggleCpanelFavorite(id: string): string[] {
  const cur = loadCpanelFavorites();
  const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
  localStorage.setItem(FAV_KEY, JSON.stringify(next));
  return next;
}

export function toolsForFeatures(
  features: Record<string, boolean> | null | undefined
): CpanelTool[] {
  return CPANEL_TOOLS.filter((t) => {
    const key = t.feature || t.nav;
    if (!features) return true;
    if (features[key] === false) return false;
    return true;
  });
}

/** Sidebar links filtered by feature list */
export function cpanelNavItems(
  features: Record<string, boolean> | null | undefined
): { key: NavKey; label: string }[] {
  const seen = new Set<NavKey>();
  const items: { key: NavKey; label: string }[] = [
    { key: "overview", label: "Home" }
  ];
  for (const sec of CPANEL_SECTIONS) {
    if (sec.id === "home" || !sec.nav) continue;
    const tools = toolsForFeatures(features).filter(
      (t) => t.section === sec.id
    );
    // Always show SSL/TLS section (sites feature)
    if (sec.id === "ssl") {
      if (features?.sites === false) continue;
      if (seen.has("ssl")) continue;
      seen.add("ssl");
      items.push({ key: "ssl", label: "SSL/TLS" });
      continue;
    }
    // FTP as its own expandable section
    if (sec.id === "ftp") {
      if (features?.files === false) continue;
      if (seen.has("ftp")) continue;
      seen.add("ftp");
      items.push({ key: "ftp", label: "FTP" });
      continue;
    }
    if (!tools.length) continue;
    if (seen.has(sec.nav)) continue;
    seen.add(sec.nav);
    items.push({ key: sec.nav, label: sec.label });
  }
  // Ensure security always if enabled
  if (!seen.has("security") && features?.security !== false) {
    items.push({ key: "security", label: "Security" });
  }
  // Ensure SSL always visible for end-users
  if (!seen.has("ssl") && features?.sites !== false) {
    items.push({ key: "ssl", label: "SSL/TLS" });
  }
  return items;
}

/** Expandable SSL submenu tools for cPanel left nav (Webuzo-style) */
export function cpanelSslTools(
  features: Record<string, boolean> | null | undefined
): CpanelTool[] {
  if (features?.sites === false) return [];
  return CPANEL_TOOLS.filter((t) => t.section === "ssl");
}

/** Expandable FTP submenu (Manage / Add / Connections) */
export function cpanelFtpTools(
  features: Record<string, boolean> | null | undefined
): CpanelTool[] {
  if (features?.files === false) return [];
  return CPANEL_TOOLS.filter((t) => t.section === "ftp");
}

/** Tools for any cPanel section (expandable sidebar) */
export function cpanelSectionTools(
  sectionId: CpanelSectionId,
  features: Record<string, boolean> | null | undefined
): CpanelTool[] {
  return toolsForFeatures(features).filter((t) => t.section === sectionId);
}

/** Map section id → default act for section header click */
export function cpanelSectionDefaultAct(sectionId: CpanelSectionId): string {
  const map: Record<CpanelSectionId, string> = {
    home: "home",
    domains: "domains_list",
    ssl: "automatic_ssl",
    databases: "mysql_databases",
    files: "file_manager",
    ftp: "ftp_accounts",
    software: "website_apps",
    mail: "email_accounts",
    advanced: "cron",
    security: "security",
    metrics: "logs",
    preferences: "appearance"
  };
  return map[sectionId] || "home";
}
